import type { DeepPartial } from "@apollo/client/utilities";
import murmur2 from "@emotion/hash";
import {
  createUserAssociation,
  decrypt,
  deleteUserAssociation,
  encrypt,
  identity,
} from "deso-protocol";
import { useContext, useEffect, useState } from "react";

import { waitForTxn } from "@/backend/api-helpers";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import type { UserAssociation } from "@/graphql/codegen/graphql";
import { CollectionsDocument } from "@/graphql/codegen/graphql";
import { useOwnQuery } from "@/hooks/use-own-query";
import { AssociationType } from "@/utils/constants";

export type CollectionBookmarkItem = {
  postHash: string;
  type: "post";
};

export type CollectionGalleryItem = {
  postHash: string;
  type: "image" | "video";
  clientId: string;
};

export type CollectionPlaylistItem = {
  postHash: string;
  type: "audio" | "video";
  clientId: string;
};

export type CollectionItem =
  | CollectionBookmarkItem
  | CollectionGalleryItem
  | CollectionPlaylistItem;

export type BaseCollectionMeta = {
  name: string;
  description: string;
  private: boolean;
  updatedAt?: string;
  id?: string;
  decrypted?: boolean;
  associationId?: string;
};

export type BookmarkCollectionMeta = BaseCollectionMeta & {
  type: "bookmark";
  items: CollectionBookmarkItem[];
};

export type PlaylistCollectionMeta = BaseCollectionMeta & {
  type: "playlist";
  items: CollectionPlaylistItem[];
};

export type GalleryCollectionMeta = BaseCollectionMeta & {
  type: "gallery";
  items: CollectionGalleryItem[];
};

export type CollectionMeta =
  | BookmarkCollectionMeta
  | PlaylistCollectionMeta
  | GalleryCollectionMeta;

const metaDataToExtraData = async (collectionMeta: CollectionMeta) => {
  if (collectionMeta.private) {
    const {
      currentUser: {
        // @ts-expect-error it's fine
        primaryDerivedKey: { messagingPublicKeyBase58Check },
      },
    } = await identity.snapshot();

    return {
      name: await encrypt(messagingPublicKeyBase58Check, collectionMeta.name),
      type: await encrypt(messagingPublicKeyBase58Check, collectionMeta.type),
      description: await encrypt(
        messagingPublicKeyBase58Check,
        collectionMeta.description,
      ),
      items: await encrypt(
        messagingPublicKeyBase58Check,
        JSON.stringify(collectionMeta.items),
      ),
      updatedAt: await encrypt(
        messagingPublicKeyBase58Check,
        collectionMeta.updatedAt || new Date().toISOString(),
      ),
      private: JSON.stringify(collectionMeta.private),
    };
  }

  return {
    name: collectionMeta.name,
    type: collectionMeta.type,
    description: collectionMeta.description,
    items: JSON.stringify(collectionMeta.items),
    // utc
    updatedAt: new Date().toISOString(),
    private: JSON.stringify(collectionMeta.private),
  };
};

const associationToMetaData = async (
  association: UserAssociation,
): Promise<CollectionMeta | null> => {
  const results = {
    name: association.extraData.name,
    type: association.extraData.type,
    description: association.extraData.description,
    updatedAt: association.extraData.updatedAt,
    id: association.associationValue,
    private: JSON.parse(association.extraData.private),
    associationId: association.associationId as string,
  };

  if (results.private) {
    const {
      currentUser: {
        // @ts-expect-error it's fine
        primaryDerivedKey: { messagingPrivateKey },
      },
    } = await identity.snapshot();

    try {
      Object.assign(results, {
        name: await decrypt(messagingPrivateKey, results.name),
        type: await decrypt(messagingPrivateKey, results.type),
        description: await decrypt(messagingPrivateKey, results.description),
        updatedAt: await decrypt(messagingPrivateKey, results.updatedAt),
        items: JSON.parse(
          await decrypt(messagingPrivateKey, association.extraData.items),
        ),
      });
    } catch (e) {
      console.error("Failed to decrypt collection association", association, e);
      return null; // If decryption fails, return null to skip this association
    }
  } else {
    Object.assign(results, {
      items: JSON.parse(association.extraData.items),
    });
  }

  return results as CollectionMeta;
};

export function useCollections({
  userPublicKey,
}: { userPublicKey?: string } = {}) {
  const { toast } = useToast();
  const { currentUserPublicKey } = useContext(UserContext);
  userPublicKey ||= currentUserPublicKey;
  const [collections, setCollections] = useState<CollectionMeta[] | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { data, setOptimistic, loading, refetch } = useOwnQuery(
    CollectionsDocument,
    {
      variables: {
        publicKey: userPublicKey,
      },
      skip: !userPublicKey,
      fetchPolicy: "cache-and-network",
    },
  );

  useEffect(() => {
    const collectionAssociations = data?.userAssociations?.nodes;

    if (!collectionAssociations) {
      return;
    }

    Promise.all(
      (collectionAssociations as UserAssociation[])
        .filter(Boolean)
        .map(associationToMetaData),
    ).then((r) => setCollections(r.filter(Boolean)));
  }, [data]);

  const upsertCollection = async (
    collectionMeta: CollectionMeta,
    collectionId?: string,
  ) => {
    const id =
      collectionId ||
      // We use the hash of a random uuid and the current user's public key to avoid collisions
      `${murmur2(crypto.randomUUID())}${murmur2(currentUserPublicKey)}`;
    const associations = (data?.userAssociations?.nodes?.filter(Boolean) ??
      []) as UserAssociation[];
    const newAssociations: DeepPartial<UserAssociation>[] =
      structuredClone(associations);
    const existingAssociation = associations.find(
      (association) => association.associationValue === id,
    );
    const extraData = await metaDataToExtraData(collectionMeta);

    // Replace the old one with the new one at the same position
    if (existingAssociation) {
      const index = newAssociations.findIndex(
        (association) => association.associationValue === id,
      );
      newAssociations[index] = {
        ...existingAssociation,
        extraData,
      };
    } else {
      newAssociations.push({
        associationId: id,
        associationValue: id,
        extraData,
        associationType: AssociationType.collection,
      } as DeepPartial<UserAssociation>);
    }

    setOptimistic({
      userAssociations: {
        nodes: newAssociations,
      },
    });

    const request = await createUserAssociation({
      AppPublicKeyBase58Check: process.env.NEXT_PUBLIC_FOCUS_APP_PUBLIC_KEY,
      AssociationType: AssociationType.collection,
      TransactorPublicKeyBase58Check: userPublicKey,
      TargetUserPublicKeyBase58Check: userPublicKey,
      AssociationValue: id,
      ExtraData: extraData,
    });

    const transactionHash = request.submittedTransactionResponse?.TxnHashHex;

    if (!transactionHash) {
      throw new Error("Missing transaction hash.");
    }

    await waitForTxn({
      transactionHash,
    });

    refetch();
  };

  const addToCollection = async (
    collectionId: string,
    item: CollectionItem,
  ) => {
    const collection = collections?.find((c) => c.id === collectionId);

    if (!collection) {
      toast({
        title: "Error",
        description: `Collection with id ${collectionId} not found.`,
        variant: "destructive",
      });
      return;
    }

    if (
      collection.items?.some(
        (i) =>
          i.postHash === item.postHash &&
          i.type === item.type &&
          // @ts-expect-error If there is no clientId, it compares undefined with undefined, which is fine
          i?.clientId === item?.clientId,
      )
    ) {
      toast({
        title: "Skipped",
        description: (
          <>
            <span className="font-bold">{collection.name}</span> already
            contains this item.
          </>
        ),
        variant: "default",
      });
      return;
    }

    const newCollection = structuredClone(collection);
    // @ts-expect-error it's fine
    newCollection.items.push(item);

    await upsertCollection(newCollection, newCollection.id);

    toast({
      title: "Success",
      description: `Item added to ${newCollection.name}.`,
      variant: "success",
    });
  };

  const removeFromCollection = async (
    collectionId: string,
    itemData: CollectionItem,
  ) => {
    const collection = collections?.find((c) => c.id === collectionId);

    if (!collection) {
      toast({
        title: "Error",
        description: `Collection with id ${collectionId} not found.`,
        variant: "destructive",
      });
      return;
    }

    const newCollection = structuredClone(collection);
    newCollection.items = newCollection.items.filter(
      // @ts-expect-error TS is quite stupid here, seems like structuredClone's types are incorrect
      (item) =>
        !(
          // prettier-ignore
          // @ts-expect-error TS is quite stupid here, seems like structuredClone's types are incorrect
          (item.postHash === itemData.postHash &&
            // @ts-expect-error TS is quite stupid here, seems like structuredClone's types are incorrect
            item.type === itemData.type &&
            // @ts-expect-error If there is no clientId, it compares undefined with undefined, which is fine
            item.clientId === itemData.clientId)
        ),
    );

    await upsertCollection(newCollection, newCollection.id);

    toast({
      title: "Success",
      description: `Item removed from ${newCollection.name}.`,
      variant: "success",
    });
  };

  const deleteCollection = async (collectionId: string) => {
    setDeleting(true);

    const collection = collections?.find((c) => c.id === collectionId);

    if (!collection) {
      toast({
        title: "Error",
        description: `Collection with id ${collectionId} not found.`,
        variant: "destructive",
      });
      setDeleting(false);
      return;
    }

    const request = await deleteUserAssociation({
      TransactorPublicKeyBase58Check: currentUserPublicKey,
      AssociationID: collection.associationId!,
    });

    const transactionHash = request.submittedTransactionResponse?.TxnHashHex;

    if (!transactionHash) {
      throw new Error("Missing transaction hash.");
    }

    await waitForTxn({
      transactionHash,
    });

    setDeleting(false);

    toast({
      title: "Success",
      description: "Collection deleted successfully.",
      variant: "success",
    });

    refetch();
  };

  return {
    upsertCollection,
    addToCollection,
    removeFromCollection,
    deleteCollection,
    collections,
    collectionsLoading: loading && !collections,
    refetchCollections: refetch,
    isOwner: userPublicKey === currentUserPublicKey,
    deleting,
  };
}
